import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getDoctors, getDailyList } from '../utils/api';
import { printRegistrationSlip } from '../utils/printSlip';

const NAVY = '#0d2137';
const GREEN = '#1a6b3c';
const RED = '#c41e3a';

export default function DoctorList() {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [doctors, setDoctors] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoctors().then(r => setDoctors(r.data)).catch(() => toast.error('Failed to load doctors'));
  }, []);

  useEffect(() => {
    setLoading(true);
    getDailyList(date)
      .then(r => setVisits(r.data))
      .catch(() => toast.error('Failed to load visits'))
      .finally(() => setLoading(false));
  }, [date]);

  const grouped = doctors.map(doc => ({
    doctor: doc,
    visits: visits.filter(v => String(v.doctor_id) === String(doc.id)),
  })).filter(g => g.visits.length > 0);

  const unassigned = visits.filter(v => !v.doctor_id || !doctors.find(d => String(d.id) === String(v.doctor_id)));

  return (
    <div style={{ padding: '32px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ color: NAVY, fontSize: 26, fontWeight: 900, marginBottom: 4 }}>Doctor-wise Patient List</h1>
          <div style={{ color: '#64748b', fontSize: 14 }}>{visits.length} total patient{visits.length !== 1 ? 's' : ''} · {grouped.length} doctor{grouped.length !== 1 ? 's' : ''}</div>
        </div>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={{ padding: '9px 14px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, outline: 'none', color: NAVY, fontWeight: 600 }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading...</div>
      ) : grouped.length === 0 && unassigned.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 15 }}>No patients on this date.</div>
      ) : (
        <>
          {grouped.map(({ doctor, visits: dVisits }) => (
            <div key={doctor.id} style={{ background: '#fff', borderRadius: 12, marginBottom: 20, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
              <div style={{ background: NAVY, color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>Dr. {doctor.name}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{doctor.specialty} · Room {doctor.room_number || '—'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: '#4ade80' }}>{dVisits.length} patients</div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: '#f87171' }}>PKR {(dVisits.length * Number(doctor.fees || 0)).toLocaleString()} total</div>
                </div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['#', 'Token', 'Patient', 'Gender', 'Age', 'Phone', 'Fees', 'Print'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700, color: '#374151', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dVisits.map((v, i) => (
                    <tr key={v.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ padding: '9px 12px', fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                      <td style={{ padding: '9px 12px', fontWeight: 700, color: RED, fontSize: 13 }}>{v.visit_token}</td>
                      <td style={{ padding: '9px 12px', fontWeight: 600, color: NAVY }}>{v.full_name || v.patient?.full_name || '—'}</td>
                      <td style={{ padding: '9px 12px', fontSize: 12 }}>{v.gender || v.patient?.gender || '—'}</td>
                      <td style={{ padding: '9px 12px', fontSize: 12 }}>{v.age_years || v.patient?.age_years ? `${v.age_years || v.patient?.age_years}y` : '—'}</td>
                      <td style={{ padding: '9px 12px', fontSize: 12 }}>{v.phone || v.patient?.phone || '—'}</td>
                      <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700, color: GREEN }}>PKR {Number(doctor.fees || 0).toLocaleString()}</td>
                      <td style={{ padding: '9px 12px' }}>
                        <button
                          onClick={() => printRegistrationSlip(v.patient || v, doctor, v.mrn, v.visit_token)}
                          style={{ padding: '4px 10px', background: NAVY, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                          Print
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {unassigned.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
              <div style={{ background: '#64748b', color: '#fff', padding: '12px 20px', fontWeight: 700 }}>
                Unassigned ({unassigned.length} patients)
              </div>
              <div style={{ padding: '12px 20px', color: '#64748b', fontSize: 13 }}>
                {unassigned.map(v => <div key={v.id}>{v.full_name || v.patient?.full_name || '—'} · {v.visit_token}</div>)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
